/**
 * Where the hotel's clutter lives.
 *
 * Two shapes. `at` puts one thing in one place because it belongs there — the
 * fire extinguisher by the dock door, the mop bucket in the corridor. `in`
 * scatters a count of something loose in a room, positioned by the seeded
 * scatter pass so the same run always looks the same.
 *
 * Nothing here is referenced by a BEO, a shortfall, a staff price or an errand,
 * and `scatter.test.js` fails if that ever stops being true. The moment a
 * mission needs one of these it is content, not clutter, and clutter is the
 * whole point: the rules need things to be general about.
 */

export const SCATTER = [
  // --- break room: the one place staff put things down ----------------------
  { in: 'break', kind: 'orange', count: 4 },
  { in: 'break', kind: 'apple', count: 3 },
  { in: 'break', kind: 'lemon', count: 2 },
  { in: 'break', kind: 'coffeecup', count: 5 },
  { in: 'break', kind: 'coffeepot', count: 1 },
  { in: 'break', kind: 'napkin', count: 6 },
  { in: 'break', kind: 'sugar', count: 5 },
  { in: 'break', kind: 'teaspoon', count: 3 },
  { in: 'break', kind: 'menu', count: 2 },
  { in: 'break', kind: 'remote', count: 1 },
  { in: 'break', kind: 'radio', count: 1 },
  { in: 'break', kind: 'ashtray', count: 1 },
  { in: 'break', kind: 'matchbook', count: 2 },
  { in: 'break', kind: 'biro', count: 3 },

  // --- corridor: what gets left in a corridor -------------------------------
  { in: 'corridor', kind: 'bucket', count: 2 },
  { in: 'corridor', kind: 'mop', count: 2 },
  { in: 'corridor', kind: 'broom', count: 2 },
  { in: 'corridor', kind: 'foldingsign', count: 3 },
  { in: 'corridor', kind: 'bustub', count: 2 },
  { in: 'corridor', kind: 'towel', count: 4 },
  { in: 'corridor', kind: 'clipboard', count: 2 },

  // --- the main run: swept, because it is the one everything comes down -----
  // Three things, all of them ankle height. Nothing tall goes here: a run with
  // something to step behind is not the run this property was given.
  { in: 'run', kind: 'towel', count: 2 },
  { in: 'run', kind: 'clipboard', count: 1 },

  // --- kitchen --------------------------------------------------------------
  { in: 'kitchen', kind: 'bottle', count: 5 },
  { in: 'kitchen', kind: 'glass', count: 6 },
  { in: 'kitchen', kind: 'wineglass', count: 6 },
  { in: 'kitchen', kind: 'carafe', count: 3 },
  { in: 'kitchen', kind: 'platter', count: 3 },
  { in: 'kitchen', kind: 'sideplate', count: 6 },
  { in: 'kitchen', kind: 'chafer', count: 3 },
  { in: 'kitchen', kind: 'coffeeurn', count: 2 },
  { in: 'kitchen', kind: 'icebucket', count: 2 },
  { in: 'kitchen', kind: 'tray', count: 4 },
  { in: 'kitchen', kind: 'bustub', count: 2 },
  { in: 'kitchen', kind: 'glassrack', count: 2 },
  { in: 'kitchen', kind: 'keg', count: 2 },

  // --- engineering shop -----------------------------------------------------
  { in: 'shop', kind: 'toolbox', count: 3 },
  { in: 'shop', kind: 'tape', count: 3 },
  { in: 'shop', kind: 'sandbag', count: 4 },
  { in: 'shop', kind: 'cinderblock', count: 5 },
  { in: 'shop', kind: 'extinguisher', count: 2 },
  { in: 'shop', kind: 'buffer', count: 1 },
  { in: 'shop', kind: 'firstaid', count: 1 },
  { in: 'shop', kind: 'wateringcan', count: 2 },

  // --- loading dock and the apron -------------------------------------------
  { in: 'dock', kind: 'cinderblock', count: 4 },
  { in: 'dock', kind: 'sandbag', count: 3 },
  { in: 'dock', kind: 'icechest', count: 2 },
  { in: 'dock', kind: 'keg', count: 3 },
  { in: 'dock', kind: 'luggage', count: 2 },
  { in: 'apron', kind: 'bottle', count: 3 },
  { in: 'apron', kind: 'cinderblock', count: 2 },

  // --- lobby: the marble, where everything is tidy and expensive ------------
  { in: 'lobby', kind: 'plant', count: 4 },
  { in: 'lobby', kind: 'budvase', count: 3 },
  { in: 'lobby', kind: 'luggage', count: 4 },
  { in: 'lobby', kind: 'menu', count: 3 },
  { in: 'lobby', kind: 'keycard', count: 2 },
  { in: 'lobby', kind: 'mirror', count: 2 },

  // --- storage and the alcove ----------------------------------------------
  { in: 'storage', kind: 'tape', count: 2 },
  { in: 'storage', kind: 'candle', count: 6 },
  { in: 'storage', kind: 'saltshaker', count: 6 },
  { in: 'storage', kind: 'coaster', count: 6 },
  { in: 'storage', kind: 'foldingsign', count: 2 },
  { in: 'alcove', kind: 'clipboard', count: 1 },
  { in: 'alcove', kind: 'biro', count: 2 },

  // --- ballroom: an empty room with a few things nobody cleared -------------
  { in: 'ballroom', kind: 'bustub', count: 1 },
  { in: 'ballroom', kind: 'napkin', count: 4 },
  { in: 'ballroom', kind: 'wineglass', count: 3 },

  // --- the manager's office -------------------------------------------------
  { in: 'office', kind: 'clipboard', count: 2 },
  { in: 'office', kind: 'biro', count: 2 },
  { in: 'office', kind: 'coffeecup', count: 2 },

  // --- things that belong where they are ------------------------------------
  { at: [30, -8], kind: 'extinguisher' },
  { at: [136, -4], kind: 'extinguisher' },
  { at: [-12, -4], kind: 'extinguisher' },
  { at: [98, 6], kind: 'firstaid' },
  { at: [88, 4], kind: 'soap' },
  { at: [62, -24], kind: 'soap' },
];

export default SCATTER;
